import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  UserPlus, UserMinus, ArrowLeftRight, Key, Shield, Clock,
  ChevronRight, AlertTriangle, Plus, X,
  Users, Activity, TrendingUp, DollarSign, Calendar, Timer,
  CheckCircle2, XCircle, Eye, RefreshCw, Search, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';

const initialJmlCases = [
  {
    id: "case_01HXYZ123ABC",
    type: "joiner",
    typeLabel: "Onboarding",
    subject: { name: "John Smith", email: "jsmith@acme.com", department: "Engineering" },
    status: "executing",
    priority: "p2",
    agents: ["identity", "endpoint", "saas"],
    progress: { completed: 2, total: 6, current: "Google Workspace provisioning" },
    sla: { remaining: "3h 30m", breached: false },
    createdAt: "2026-01-15T09:00:00Z"
  },
  {
    id: "case_02HXYZ456DEF",
    type: "leaver",
    typeLabel: "Offboarding",
    subject: { name: "Jane Doe", email: "jdoe@acme.com", department: "Sales" },
    status: "pending_approval",
    priority: "p1",
    agents: ["identity", "endpoint", "saas", "security"],
    approvalNeeded: "Security Team",
    reason: "Involuntary termination - immediate access revocation required",
    sla: { remaining: "1h 45m", breached: false },
    createdAt: "2026-01-15T08:30:00Z"
  },
  {
    id: "case_03HXYZ789GHI",
    type: "access",
    typeLabel: "Access Request",
    subject: { name: "Mike Lee", email: "mlee@acme.com", department: "Engineering" },
    status: "pending_approval",
    priority: "p2",
    agents: ["identity", "security"],
    approvalNeeded: "Security Team",
    reason: "Admin access to production database",
    sla: { remaining: "3h 30m", breached: false },
    createdAt: "2026-01-15T08:15:00Z"
  },
  {
    id: "case_06HXYZ111AAA",
    type: "mover",
    typeLabel: "Department Transfer",
    subject: { name: "Sara Chen", email: "schen@acme.com", department: "Marketing → Engineering" },
    status: "verifying",
    priority: "p2",
    agents: ["identity", "saas"],
    progress: { completed: 3, total: 4, current: "Verifying new access grants" },
    sla: { remaining: "2h 15m", breached: false },
    createdAt: "2026-01-15T07:00:00Z"
  },
  {
    id: "case_07HXYZ222BBB",
    type: "joiner",
    typeLabel: "Onboarding",
    subject: { name: "Alex Kim", email: "akim@acme.com", department: "Product" },
    status: "closed",
    priority: "p2",
    agents: ["identity", "endpoint", "saas"],
    resolution: "Completed in 3h 45m",
    closedAt: "2026-01-14T16:00:00Z",
    createdAt: "2026-01-14T12:15:00Z"
  }
];

const policyViolations = [
  {
    id: "viol_001",
    caseId: "case_viol_001",
    type: "stale_access",
    severity: "medium",
    title: "12 users with unused admin access > 90 days",
    recommendation: "Propose access reclamation bundle",
    affectedUsers: 12
  },
  {
    id: "viol_002",
    caseId: "case_viol_002",
    type: "orphaned_account",
    severity: "high",
    title: "3 accounts with no HR record match",
    recommendation: "Investigate and propose deactivation",
    affectedUsers: 3
  },
  {
    id: "viol_003",
    caseId: "case_viol_003",
    type: "missing_mfa",
    severity: "high",
    title: "8 privileged users without MFA enabled",
    recommendation: "Propose MFA enforcement bundle",
    affectedUsers: 8
  }
];

const accessReviews = [
  {
    id: "review_001",
    name: "Q1 2026 SOX Access Review",
    type: "quarterly",
    status: "in_progress",
    dueDate: "2026-01-31",
    progress: { completed: 45, total: 120 },
    reviewer: "IT Security Team",
    scope: "Finance applications"
  },
  {
    id: "review_002",
    name: "Production DB Access Certification",
    type: "monthly",
    status: "pending",
    dueDate: "2026-02-15",
    progress: { completed: 0, total: 28 },
    reviewer: "Engineering Managers",
    scope: "Production databases"
  },
  {
    id: "review_003",
    name: "Admin Privilege Review",
    type: "quarterly",
    status: "completed",
    dueDate: "2025-12-31",
    progress: { completed: 85, total: 85 },
    reviewer: "Security Team",
    scope: "All admin accounts",
    completedDate: "2025-12-28"
  }
];

const expiringAccess = [
  {
    id: "exp_001",
    user: { name: "David Park", email: "dpark@acme.com" },
    access: "Production SSH Access",
    grantedFor: "Sprint 47 debugging",
    expiresAt: "2026-01-20T18:00:00Z",
    daysLeft: 1,
    canExtend: true
  },
  {
    id: "exp_002",
    user: { name: "Lisa Wang", email: "lwang@acme.com" },
    access: "AWS Admin Console",
    grantedFor: "Migration project",
    expiresAt: "2026-01-25T23:59:00Z",
    daysLeft: 6,
    canExtend: true
  },
  {
    id: "exp_003",
    user: { name: "Tom Brown", email: "tbrown@acme.com" },
    access: "Salesforce Admin",
    grantedFor: "Q4 audit support",
    expiresAt: "2026-01-22T23:59:00Z",
    daysLeft: 3,
    canExtend: false
  }
];

const peerAccessComparison = [
  {
    id: "peer_001",
    requester: { name: "Mike Lee", email: "mlee@acme.com", role: "Senior Engineer" },
    requestedAccess: "Datadog Admin",
    peerGroup: "Senior Engineers in Platform Team",
    peersWithAccess: 8,
    totalPeers: 12,
    recommendation: "approve",
    reasoning: "67% of peers have this access. Common for on-call duties."
  },
  {
    id: "peer_002",
    requester: { name: "Sarah Kim", email: "skim@acme.com", role: "Product Manager" },
    requestedAccess: "GitHub Write Access",
    peerGroup: "Product Managers",
    peersWithAccess: 2,
    totalPeers: 15,
    recommendation: "review",
    reasoning: "Only 13% of peers have this access. Unusual for this role."
  }
];

const typeIcons = {
  joiner: UserPlus,
  leaver: UserMinus,
  mover: ArrowLeftRight,
  access: Key
};

const typeLabels = {
  joiner: 'Onboarding',
  leaver: 'Offboarding',
  mover: 'Department Transfer',
  access: 'Access Request'
};

import { getLogoByIntegrationId } from '../constants/logos';

const NewCaseModal = ({ isOpen, onClose, onCreateCase }) => {
  const [step, setStep] = useState(1);
  const [caseType, setCaseType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    manager: '',
    reason: '',
    duration: 'permanent'
  });

  if (!isOpen) return null;

  const handleCreate = () => {
    const newCase = {
      id: `case_${Date.now()}`,
      type: caseType,
      typeLabel: typeLabels[caseType],
      subject: {
        name: formData.name,
        email: formData.email,
        department: formData.department
      },
      status: 'pending_approval',
      priority: 'p2',
      agents: caseType === 'joiner' ? ['identity', 'endpoint', 'saas'] :
        caseType === 'leaver' ? ['identity', 'endpoint', 'saas', 'security'] :
          caseType === 'mover' ? ['identity', 'saas'] : ['identity', 'security'],
      approvalNeeded: 'Manager',
      reason: formData.reason || `New ${typeLabels[caseType]} request`,
      sla: { remaining: '4h', breached: false },
      createdAt: new Date().toISOString(),
      duration: formData.duration
    };
    onCreateCase(newCase);
    onClose();
    setStep(1);
    setCaseType('');
    setFormData({ name: '', email: '', department: '', manager: '', reason: '', duration: 'permanent' });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">
            {step === 1 ? 'New JML Case' : `New ${typeLabels[caseType]}`}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-4">
          {step === 1 && (
            <div>
              <p className="text-sm text-slate-600 mb-3">Select the type of JML case to create:</p>
              <div className="grid grid-cols-2 gap-2">
                {['joiner', 'leaver', 'mover', 'access'].map((type) => {
                  const Icon = typeIcons[type];
                  return (
                    <button
                      key={type}
                      onClick={() => { setCaseType(type); setStep(2); }}
                      className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg hover:border-primary hover:bg-slate-50 transition-colors"
                    >
                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium text-slate-900">{typeLabels[type]}</div>
                        <div className="text-xs text-slate-500">
                          {type === 'joiner' && 'New employee'}
                          {type === 'leaver' && 'Termination'}
                          {type === 'mover' && 'Role change'}
                          {type === 'access' && 'Access request'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jsmith@acme.com"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                <select
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="">Select department</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Product">Product</option>
                  <option value="Sales">Sales</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Finance">Finance</option>
                  <option value="HR">HR</option>
                </select>
              </div>
              {caseType === 'access' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Access Duration</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: '24h', label: '24 Hours' },
                      { value: '7d', label: '7 Days' },
                      { value: '30d', label: '30 Days' },
                      { value: '90d', label: '90 Days' },
                      { value: 'permanent', label: 'Permanent' },
                      { value: 'custom', label: 'Custom' }
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, duration: opt.value })}
                        className={`px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${formData.duration === opt.value
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                          }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {(caseType === 'access' || caseType === 'mover') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason / Justification</label>
                  <textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Explain the request..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          {step === 2 ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                size="sm"
                onClick={handleCreate}
                disabled={!formData.name || !formData.email}
              >
                Create Case
              </Button>
            </>
          ) : (
            <>
              <div />
              <Button variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AccessReviewModal = ({ isOpen, onClose, review }) => {
  const [decisions, setDecisions] = useState({});

  if (!isOpen || !review) return null;

  const mockEntitlements = [
    { id: 'ent_1', user: 'john.smith@acme.com', entitlement: 'Salesforce Admin', lastUsed: '2 days ago', recommendation: 'keep', logoId: 'salesforce' },
    { id: 'ent_2', user: 'jane.doe@acme.com', entitlement: 'AWS Console Access', lastUsed: '45 days ago', recommendation: 'review', logoId: 'aws' },
    { id: 'ent_3', user: 'mike.lee@acme.com', entitlement: 'GitHub Admin', lastUsed: '12 hours ago', recommendation: 'keep', logoId: 'github' },
    { id: 'ent_4', user: 'sara.chen@acme.com', entitlement: 'Jira Admin', lastUsed: '90+ days', recommendation: 'revoke', logoId: 'jira-sm' },
    { id: 'ent_5', user: 'alex.kim@acme.com', entitlement: 'Datadog Admin', lastUsed: '5 days ago', recommendation: 'keep', logoId: 'datadog' },
  ];

  const handleDecision = (entId, decision) => {
    setDecisions({ ...decisions, [entId]: decision });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">{review.name}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{review.scope} • Due {review.dueDate}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search entitlements..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-1" />
              Filter
            </Button>
          </div>

          <div className="space-y-2">
            {mockEntitlements.map((ent) => (
              <div key={ent.id} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">{ent.user}</div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    {ent.logoId && (
                      <img src={getLogoByIntegrationId(ent.logoId)?.src} alt="" className="w-3.5 h-3.5 object-contain" />
                    )}
                    {ent.entitlement}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">Last used: {ent.lastUsed}</div>
                </div>
                <div className={`text-xs px-2 py-0.5 rounded ${ent.recommendation === 'keep' ? 'bg-primary/10 text-primary' :
                  ent.recommendation === 'revoke' ? 'bg-slate-200 text-slate-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>
                  {ent.recommendation === 'keep' ? 'AI: Keep' :
                    ent.recommendation === 'revoke' ? 'AI: Revoke' : 'AI: Review'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleDecision(ent.id, 'approve')}
                    className={`p-1.5 rounded transition-colors ${decisions[ent.id] === 'approve' ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-400'
                      }`}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDecision(ent.id, 'revoke')}
                    className={`p-1.5 rounded transition-colors ${decisions[ent.id] === 'revoke' ? 'bg-slate-700 text-white' : 'hover:bg-slate-100 text-slate-400'
                      }`}
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <div className="text-xs text-slate-500">
            {Object.keys(decisions).length} of {mockEntitlements.length} reviewed
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm">Submit Decisions</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Identity = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [jmlCases, setJmlCases] = useState(initialJmlCases);
  const [activeTab, setActiveTab] = useState('cases');
  const [selectedReview, setSelectedReview] = useState(null);

  const handleCreateCase = (newCase) => {
    setJmlCases([newCase, ...jmlCases]);
  };

  const filteredCases = jmlCases.filter(c => {
    if (filter !== 'all' && c.type !== filter) return false;
    if (statusFilter === 'active' && c.status === 'closed') return false;
    if (statusFilter === 'closed' && c.status !== 'closed') return false;
    return true;
  });

  const activeCases = useMemo(() => jmlCases.filter(c => c.status !== 'closed'), [jmlCases]);
  const needsApproval = useMemo(() => activeCases.filter(c => c.status === 'pending_approval'), [activeCases]);

  const identityMetrics = {
    activeCases: activeCases.length,
    pendingApprovals: needsApproval.length,
    avgOnboardTime: "3h 45m",
    costPerOnboard: "$0.82",
    costPerOffboard: "$1.15",
    offboardCompleteness: "99.2%",
    weeklyJoiners: jmlCases.filter(c => c.type === 'joiner').length,
    weeklyLeavers: jmlCases.filter(c => c.type === 'leaver').length,
    weeklyMovers: jmlCases.filter(c => c.type === 'mover').length,
    pendingReviews: accessReviews.filter(r => r.status !== 'completed').length,
    expiringAccess: expiringAccess.length
  };

  const getStatusBadge = (status) => {
    const config = {
      executing: 'badge-executing',
      pending_approval: 'badge-pending',
      verifying: 'badge-verifying',
      closed: 'badge-active',
      failed: 'badge-failed'
    };
    const labels = {
      executing: 'Executing',
      pending_approval: 'Needs Approval',
      verifying: 'Verifying',
      closed: 'Completed',
      failed: 'Failed'
    };
    return { className: config[status] || 'badge-neutral', label: labels[status] || status };
  };

  const getPriorityColor = (priority) => {
    const colors = { p1: 'bg-slate-900', p2: 'bg-slate-400', p3: 'bg-primary' };
    return colors[priority] || 'bg-slate-400';
  };

  return (
    <div className="space-y-6" data-testid="identity-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Key className="w-5 h-5 text-slate-500" />
            Identity
          </h1>
          <p className="text-sm text-slate-500 mt-1">Joiner-Mover-Leaver autopilot and access governance</p>
        </div>
        <Button onClick={() => setShowNewCaseModal(true)} size="sm" data-testid="new-jml-case-btn">
          <Plus className="w-4 h-4 mr-1" />
          New JML Case
        </Button>
      </div>

      {needsApproval.length > 0 && (
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-4" data-testid="needs-decision-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-slate-700 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-slate-900">
                {needsApproval.length} action bundle{needsApproval.length > 1 ? 's' : ''} awaiting decision
              </h3>
              <p className="text-sm text-slate-700 mt-1">
                {needsApproval[0].typeLabel}: {needsApproval[0].subject.name} - {needsApproval[0].reason}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="bg-slate-200 border-slate-400 text-slate-800 hover:bg-slate-300"
                  onClick={() => navigate(`/case/${needsApproval[0].id}/review`)}
                >
                  Review Bundle
                </Button>
                <span className="text-xs text-slate-600">SLA: {needsApproval[0].sla.remaining} remaining</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Active Cases</span>
            <Activity className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.activeCases}</div>
          <div className="text-xs text-slate-400 mt-1">{identityMetrics.pendingApprovals} pending approval</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Avg Onboard</span>
            <Clock className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.avgOnboardTime}</div>
          <div className="text-xs text-primary mt-1">Target: &lt; 4h</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Cost/Onboard</span>
            <DollarSign className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.costPerOnboard}</div>
          <div className="text-xs text-slate-400 mt-1">Offboard: {identityMetrics.costPerOffboard}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Completeness</span>
            <Shield className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.offboardCompleteness}</div>
          <div className="text-xs text-primary mt-1">Zero lingering access</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Access Reviews</span>
            <Eye className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.pendingReviews}</div>
          <div className="text-xs text-slate-400 mt-1">pending certifications</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Expiring Soon</span>
            <Timer className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{identityMetrics.expiringAccess}</div>
          <div className="text-xs text-slate-400 mt-1">time-bound access grants</div>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 w-fit">
        {[
          { key: 'cases', label: 'JML Cases' },
          { key: 'reviews', label: 'Access Reviews' },
          { key: 'expiring', label: 'Expiring Access' },
          { key: 'peer', label: 'Peer Comparison' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${activeTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'cases' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-slate-900">JML Cases</h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                    {['all', 'joiner', 'mover', 'leaver', 'access'].map((f) => (
                      <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-2 py-1 text-xs font-medium rounded capitalize transition-colors ${filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        {f === 'all' ? 'All' : f}
                      </button>
                    ))}
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="active">Active</option>
                    <option value="closed">Completed</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                {filteredCases.length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-500 text-sm">
                    No cases match your filters
                  </div>
                ) : (
                  filteredCases.map((caseItem) => {
                    const TypeIcon = typeIcons[caseItem.type];
                    const statusBadge = getStatusBadge(caseItem.status);

                    return (
                      <Link
                        key={caseItem.id}
                        to={`/case/${caseItem.id}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors"
                        data-testid={`jml-case-${caseItem.id}`}
                      >
                        <div className={`w-1 h-10 rounded-full ${getPriorityColor(caseItem.priority)}`} />
                        <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                          <TypeIcon className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`badge-status ${statusBadge.className}`}>
                              {statusBadge.label}
                            </span>
                            <span className="text-sm font-medium text-slate-900 truncate">
                              {caseItem.typeLabel}: {caseItem.subject.name}
                            </span>
                            {caseItem.duration && caseItem.duration !== 'permanent' && (
                              <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                                <Timer className="w-3 h-3 inline mr-0.5" />
                                {caseItem.duration}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {caseItem.subject.department}
                            {caseItem.progress && (
                              <span className="ml-2 text-primary">
                                {caseItem.progress.completed}/{caseItem.progress.total} actions - {caseItem.progress.current}
                              </span>
                            )}
                            {caseItem.approvalNeeded && (
                              <span className="ml-2 text-slate-600">
                                Awaiting {caseItem.approvalNeeded}
                              </span>
                            )}
                            {caseItem.resolution && (
                              <span className="ml-2 text-primary">{caseItem.resolution}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-1">
                            {caseItem.agents.map((agent) => (
                              <span key={agent} className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">
                                {agent}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          {caseItem.sla && (
                            <div className={`text-xs ${caseItem.sla.breached ? 'text-slate-700' : 'text-slate-400'}`}>
                              {caseItem.sla.remaining}
                            </div>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-slate-600" />
                  Policy Violations
                </h2>
              </div>
              <div className="divide-y divide-slate-100">
                {policyViolations.map((violation) => (
                  <div key={violation.id} className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <div className={`w-2 h-2 rounded-full mt-1.5 ${violation.severity === 'high' ? 'bg-slate-900' : 'bg-slate-400'}`} />
                      <div className="flex-1">
                        <p className="text-sm text-slate-900">{violation.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{violation.recommendation}</p>
                        <button
                          onClick={() => navigate(`/case/${violation.caseId}/review`)}
                          className="mt-2 text-xs text-primary hover:underline"
                          data-testid={`propose-bundle-${violation.id}`}
                        >
                          Propose action bundle
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-slate-200">
              <div className="px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  Identity Agent
                </h2>
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Trust Score</span>
                  <span className="text-sm font-medium text-primary">98.2%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Actions (24h)</span>
                  <span className="text-sm font-medium text-slate-900">47</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Rollback Rate</span>
                  <span className="text-sm font-medium text-slate-900">0.6%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Connectors</span>
                  <span className="text-sm text-slate-600">Okta, Entra ID</span>
                </div>
                <Link to="/agents" className="block mt-3 text-xs text-primary hover:underline">
                  View agent details
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reviews' && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Access Reviews & Certifications</h2>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-1" />
              New Campaign
            </Button>
          </div>
          <div className="divide-y divide-slate-100">
            {accessReviews.map((review) => (
              <div key={review.id} className="flex items-center gap-4 px-4 py-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${review.status === 'completed' ? 'bg-primary/10' :
                  review.status === 'in_progress' ? 'bg-slate-100' : 'bg-slate-50'
                  }`}>
                  {review.status === 'completed' ? (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  ) : (
                    <Calendar className="w-5 h-5 text-slate-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{review.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${review.status === 'completed' ? 'bg-primary/10 text-primary' :
                      review.status === 'in_progress' ? 'bg-slate-200 text-slate-700' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                      {review.status === 'completed' ? 'Completed' :
                        review.status === 'in_progress' ? 'In Progress' : 'Pending'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {review.scope} • {review.reviewer}
                  </div>
                  {review.status !== 'completed' && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${(review.progress.completed / review.progress.total) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">
                          {review.progress.completed}/{review.progress.total}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500">
                    {review.status === 'completed' ? `Completed ${review.completedDate}` : `Due ${review.dueDate}`}
                  </div>
                </div>
                {review.status !== 'completed' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedReview(review)}
                  >
                    Review
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'expiring' && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Time-Bound Access Expiring Soon</h2>
          </div>
          <div className="divide-y divide-slate-100">
            {expiringAccess.map((item) => (
              <div key={item.id} className="flex items-center gap-4 px-4 py-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.daysLeft <= 1 ? 'bg-slate-200' : item.daysLeft <= 3 ? 'bg-slate-100' : 'bg-slate-50'
                  }`}>
                  <Timer className={`w-5 h-5 ${item.daysLeft <= 1 ? 'text-slate-700' : 'text-slate-500'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900">{item.user.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${item.daysLeft <= 1 ? 'bg-slate-200 text-slate-700' :
                      item.daysLeft <= 3 ? 'bg-slate-100 text-slate-600' :
                        'bg-slate-50 text-slate-500'
                      }`}>
                      {item.daysLeft === 1 ? 'Expires tomorrow' : `${item.daysLeft} days left`}
                    </span>
                  </div>
                  <div className="text-sm text-slate-700 mt-0.5">{item.access}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Granted for: {item.grantedFor}</div>
                </div>
                <div className="flex items-center gap-2">
                  {item.canExtend ? (
                    <>
                      <Button variant="outline" size="sm">
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Extend
                      </Button>
                      <Button variant="ghost" size="sm" className="text-slate-500">
                        Let Expire
                      </Button>
                    </>
                  ) : (
                    <span className="text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded">
                      Cannot extend
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'peer' && (
        <div className="bg-white rounded-lg border border-slate-200">
          <div className="px-4 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-900">Peer Access Comparison</h2>
            <p className="text-xs text-slate-500 mt-0.5">AI-powered analysis of access requests against peer groups</p>
          </div>
          <div className="divide-y divide-slate-100">
            {peerAccessComparison.map((item) => (
              <div key={item.id} className="px-4 py-4">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.recommendation === 'approve' ? 'bg-primary/10' : 'bg-slate-100'
                    }`}>
                    <Users className={`w-5 h-5 ${item.recommendation === 'approve' ? 'text-primary' : 'text-slate-500'
                      }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{item.requester.name}</span>
                      <span className="text-xs text-slate-500">({item.requester.role})</span>
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">
                      Requesting: <span className="font-medium">{item.requestedAccess}</span>
                    </div>
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-700">{item.peerGroup}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${item.recommendation === 'approve' ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-700'
                          }`}>
                          {item.recommendation === 'approve' ? 'Recommend Approve' : 'Needs Review'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${item.recommendation === 'approve' ? 'bg-primary' : 'bg-slate-400'
                                }`}
                              style={{ width: `${(item.peersWithAccess / item.totalPeers) * 100}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-xs text-slate-600">
                          {item.peersWithAccess}/{item.totalPeers} peers
                        </span>
                      </div>
                      <p className="text-xs text-slate-600 mt-2">{item.reasoning}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      className={item.recommendation === 'approve' ? '' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button variant="outline" size="sm">
                      <XCircle className="w-4 h-4 mr-1" />
                      Deny
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <NewCaseModal
        isOpen={showNewCaseModal}
        onClose={() => setShowNewCaseModal(false)}
        onCreateCase={handleCreateCase}
      />

      <AccessReviewModal
        isOpen={!!selectedReview}
        onClose={() => setSelectedReview(null)}
        review={selectedReview}
      />
    </div>
  );
};

export default Identity;
